import { apiPath } from '@/redux/apiPath';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { ThunkConfig } from '@/types/store';
import { getError } from '@/utils/error-report/getError';
import { axiosInstanceNewAPI } from '@/services/api/utils/axiosInstance';
import { ModelElement, ModelElements, AnnotationData } from '@/types/models';
import { MODEL_ELEMENTS_FETCHING_ERROR_PREFIX } from '@/redux/modelElements/modelElements.constants';
import { modelElementSchema } from '@/models/modelElementSchema';
import { pageableSchema } from '@/models/pageableSchema';
import { UpdateModelElementData } from '@/redux/modelElements/modelElements.types';

export const getModelElement = createAsyncThunk<
  ModelElement | undefined,
  { modelId: number; elementId: number },
  ThunkConfig
>('modelElements/getModelElement', async ({ modelId, elementId }) => {
  try {
    const { data } = await axiosInstanceNewAPI.get<ModelElement>(
      apiPath.getModelElement(modelId, elementId),
    );
    const isDataValid = validateDataUsingZodSchema(data, modelElementSchema);
    return isDataValid ? data : undefined;
  } catch (error) {
    return Promise.reject(getError({ error, prefix: MODEL_ELEMENTS_FETCHING_ERROR_PREFIX }));
  }
});

export const getModelElementsForModel = createAsyncThunk<
  Array<ModelElement> | undefined,
  number,
  ThunkConfig
>('modelElements/getModelElementsForModel', async (modelId: number) => {
  try {
    const { data } = await axiosInstanceNewAPI.get<ModelElements>(
      apiPath.getModelElements(modelId),
    );
    const isDataValid = validateDataUsingZodSchema(data, pageableSchema(modelElementSchema));
    return isDataValid ? data.content : undefined;
  } catch (error) {
    return Promise.reject(getError({ error, prefix: MODEL_ELEMENTS_FETCHING_ERROR_PREFIX }));
  }
});

export const updateElementGlyph = createAsyncThunk<
  null,
  {
    modelId: number;
    elementId: number;
    glyphId: number | null;
  },
  ThunkConfig
>('modelElements/updateElementGlyph', async ({ modelId, elementId, glyphId }) => {
  try {
    await axiosInstanceNewAPI.post(apiPath.updateElementGlyph(modelId, elementId), { id: glyphId });
    return null;
  } catch (error) {
    return Promise.reject(getError({ error }));
  }
});

export const updateElementSubmap = createAsyncThunk<
  null,
  {
    modelId: number;
    elementId: number;
    submapId: number | null;
  },
  ThunkConfig
>('modelElements/updateElementSubmap', async ({ modelId, elementId, submapId }) => {
  try {
    await axiosInstanceNewAPI.post(apiPath.updateElementSubmap(modelId, elementId), {
      id: submapId,
    });
    return null;
  } catch (error) {
    return Promise.reject(getError({ error }));
  }
});

export const updateElementMeta = createAsyncThunk<
  null,
  {
    element: ModelElement;
    data: UpdateModelElementData;
  },
  ThunkConfig
>('modelElements/updateElementData', async ({ element, data }) => {
  try {
    const payload = {
      ...element,
    };

    if (data.immediateLink) {
      payload.immediateLink = data.immediateLink;
    } else {
      payload.immediateLink = undefined;
    }

    if (data.fontName) {
      payload.fontName = data.fontName;
    }

    if (data.name !== undefined) {
      payload.name = data.name;
    }
    if (data.notes !== undefined) {
      payload.notes = data.notes;
    }
    if (data.fontSize !== undefined) {
      payload.fontSize = data.fontSize;
    }
    if (data.fontColor !== undefined) {
      payload.fontColor = data.fontColor;
    }
    if (data.fillColor !== undefined) {
      payload.fillColor = data.fillColor;
    }
    if (data.borderColor !== undefined) {
      payload.borderColor = data.borderColor;
    }
    if (data.nameHorizontalAlign !== undefined) {
      payload.nameHorizontalAlign = data.nameHorizontalAlign;
    }
    if (data.nameVerticalAlign !== undefined) {
      payload.nameVerticalAlign = data.nameVerticalAlign;
    }
    if (data.lineWidth !== undefined) {
      payload.lineWidth = data.lineWidth;
    }
    if (data.innerWidth !== undefined) {
      payload.innerWidth = data.innerWidth;
    }
    if (data.outerWidth !== undefined) {
      payload.outerWidth = data.outerWidth;
    }
    if (data.thickness !== undefined) {
      payload.thickness = data.thickness;
    }
    if (data.z !== undefined) {
      payload.z = data.z;
    }
    if (data.allowUiInteraction !== undefined) {
      payload.allowUiInteraction = data.allowUiInteraction;
    }

    await axiosInstanceNewAPI.put(apiPath.updateElement(element.map, element.id), payload);
    return null;
  } catch (error) {
    return Promise.reject(getError({ error }));
  }
});

export const addElementAnnotation = createAsyncThunk<
  null,
  { modelId: number; elementId: number; annotationData: AnnotationData },
  ThunkConfig
>('modelElements/addElementAnnotation', async ({ modelId, elementId, annotationData }) => {
  try {
    await axiosInstanceNewAPI.post(
      apiPath.addElementAnnotation(modelId, elementId),
      annotationData,
    );
    return null;
  } catch (error) {
    return Promise.reject(getError({ error }));
  }
});

export const updateElementAnnotation = createAsyncThunk<
  null,
  { modelId: number; elementId: number; annotationId: number; annotationData: AnnotationData },
  ThunkConfig
>(
  'modelElements/updateElementAnnotation',
  async ({ modelId, elementId, annotationId, annotationData }) => {
    try {
      await axiosInstanceNewAPI.put(
        apiPath.updateElementAnnotation(modelId, elementId, annotationId),
        annotationData,
      );
      return null;
    } catch (error) {
      return Promise.reject(getError({ error }));
    }
  },
);

export const deleteElementAnnotation = createAsyncThunk<
  null,
  { modelId: number; elementId: number; annotationId: number },
  ThunkConfig
>('modelElements/deleteElementAnnotation', async ({ modelId, elementId, annotationId }) => {
  try {
    await axiosInstanceNewAPI.delete(
      apiPath.deleteElementAnnotation(modelId, elementId, annotationId),
    );
    return null;
  } catch (error) {
    return Promise.reject(getError({ error }));
  }
});
